import { PropsWithChildren, useState } from 'react'

import {
  Box,
  BoxList,
  Button as DesignSystemButton,
  Icon,
  InputCheckBox,
  MediumIcon,
  SearchField,
  SortDirection,
  Table,
  Text,
  matchText,
} from '@factorialco/deprecated-design-system'
import { compact, orderBy, xor } from 'lodash'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import createComputed from 'modules/core/hooks/createComputed'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

type Option = { label: string; name: string }

interface Props<T> {
  options: T[]
  icon?: MediumIcon
  multiple?: boolean
  onAdd: (added: T) => void
}

interface ButtonProps {
  icon?: MediumIcon
  disabled?: boolean
  onClick?: () => void
}

interface OptionProps {
  label: string
  selected: boolean
  checkable: boolean
  onClick: () => void
}

const useComputedOptions = createComputed(
  (options: Option[], search: string, order: SortDirection): Option[] => {
    const filteredOptions = options.filter((option) => matchText(search, option.label))
    return orderBy(filteredOptions, ['label'], [order])
  }
)

const Button = ({ disabled, icon, children, onClick }: PropsWithChildren<ButtonProps>) => {
  const [hover, setHover] = useState(false)

  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      paddingX="s24"
      paddingY="s8"
      background={disabled ? 'grey100' : hover ? 'grey200' : 'grey100'}
      border={{ all: { style: 'dashed', width: 's2', color: 'grey400' } }}
      borderRadius={{ all: 'abs008' }}
      justifyContent="spaceBetween"
      alignItems="center"
      style={{
        borderSpacing: '240px',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onClick={onClick}
    >
      <Box flexDirection="row" alignItems="center" gap="s6" justifyContent="center">
        {icon && <Icon.Medium icon={icon} color={disabled ? 'grey400' : 'grey700'} />}
        <Text.Normal size="200" weight="medium" color={disabled ? 'grey400' : 'grey800'}>
          {children}
        </Text.Normal>
      </Box>
    </Box>
  )
}

const Option = ({ label, selected, checkable, onClick }: OptionProps) => (
  <Box
    flexDirection="row"
    alignItems="center"
    cursor="pointer"
    gap="s16"
    paddingX="s16"
    paddingY="s12"
    width="full"
    onClick={onClick}
  >
    {checkable && <InputCheckBox checked={selected} />}
    <Text.Normal weight="medium">{label}</Text.Normal>
  </Box>
)

const AddButton = <T extends Option>({
  options,
  icon,
  children,
  multiple = false,
  onAdd,
}: PropsWithChildren<Props<T>>) => {
  const { search, onSearch } = useDebouncedSearch()
  const [direction, setDirection] = useState<SortDirection>('asc')
  const [selectedFields, setSelectedFields] = useState<string[]>([])

  const computedOptions = useComputedOptions(options, search, direction).get()
  const disabled = options.length === 0

  const selectedAll = options.every((o) => selectedFields.includes(o.name))

  const OptionsWrapper = multiple ? Box : PopoverMenu.Close

  const onSubmit = (fields: string[]) =>
    compact(fields.map((name) => options.find((o) => o.name === name))).forEach(onAdd)

  return (
    <PopoverMenu type="dialog" onClick={() => setSelectedFields([])}>
      <PopoverMenu.Trigger disabled={disabled}>
        <Button icon={icon} disabled={disabled}>
          {children}
        </Button>
      </PopoverMenu.Trigger>

      <PopoverMenu.Content side="bottom" align="center" width="full" focusOnOpen={false}>
        <Box paddingY="s12" paddingX="s24" style={{ width: '550px' }} gap="s16">
          <BoxList>
            <Box width="full" flexDirection="row" background="grey200" padding="s8">
              <SearchField
                variant="big"
                search={search}
                onSearch={onSearch}
                placeholder={i18n.t('common.search')}
              />
            </Box>

            <Box
              alignItems="center"
              background="grey200"
              flexDirection="row"
              gap="s16"
              paddingX="s16"
              paddingY="s12"
              width="full"
            >
              {multiple && (
                <InputCheckBox
                  checked={selectedAll}
                  onClick={() => setSelectedFields(selectedAll ? [] : options.map((o) => o.name))}
                />
              )}

              <Table.SortingHeader
                selected={direction === 'asc'}
                onClick={() => {
                  direction === 'asc' ? setDirection('desc') : setDirection('asc')
                }}
              >
                {i18n.t('common.name')}
              </Table.SortingHeader>
            </Box>

            <Box maxHeight="s200" width="full" overflowY="auto">
              {computedOptions.length === 0 && (
                <Box alignItems="center" padding="s40" width="full" cursor="default">
                  <Text.Normal>{i18n.t('universal_exports.config.fields.not_found')}</Text.Normal>
                </Box>
              )}
              <OptionsWrapper>
                {computedOptions.map((option) => (
                  <Option
                    key={option.name}
                    label={option.label}
                    selected={selectedFields.includes(option.name)}
                    checkable={multiple}
                    onClick={() =>
                      multiple
                        ? setSelectedFields((fields) => xor(fields, [option.name]))
                        : onSubmit([option.name])
                    }
                  />
                ))}
              </OptionsWrapper>
            </Box>
          </BoxList>

          {multiple && (
            <Box alignItems="flexEnd">
              <PopoverMenu.Close>
                <DesignSystemButton
                  onClick={() => onSubmit(selectedFields)}
                  disabled={selectedFields.length === 0}
                >
                  {i18n.t('universal_exports.config.fields.add_columns')}
                </DesignSystemButton>
              </PopoverMenu.Close>
            </Box>
          )}
        </Box>
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

export default AddButton
